import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { IsActiveMatchOptions, RouterModule } from '@angular/router';
import { TopLevelMenuItems } from '@enums/toplevel-menu-items.enum';
import { UserService } from 'src/app/shared/services/user.service';
import { environment } from 'src/environments/environment';

interface IPageNavItem {
  label: string;
  route: string;
  visible: boolean;
  children?: IPageNavItem[];
  expanded?: boolean;
}

/** Active when the link URL is a prefix of the current URL (includes child routes). */
const ROUTER_LINK_ACTIVE_OPTIONS: IsActiveMatchOptions = {
  paths: 'subset',
  queryParams: 'ignored',
  fragment: 'ignored',
  matrixParams: 'ignored',
};

@Component({
  selector: 'app-page-sidebar-menu',
  imports: [CommonModule, RouterModule],
  templateUrl: './page-sidebar-menu.component.html',
  styleUrls: ['./page-sidebar-menu.component.scss'],
})
export class PageSidebarMenuComponent implements OnInit, OnChanges {
  @Input() isSidebarExpanded: boolean = false;
  @Input() selectedPageName: string = '';

  readonly routerLinkActiveOptions = ROUTER_LINK_ACTIVE_OPTIONS;

  menuItems: IPageNavItem[] = [];
  isSubTenant: boolean = false;
  isSuperTenant: boolean = false;

  constructor(private readonly userService: UserService) {}

  ngOnInit(): void {
    this.rebuildMenuItems();
  }

  /**
   * Parent keeps one sidebar instance while toggling admin <=> financing; inputs update
   * without destroying the component, so menu data must refresh here (not only in {@link ngOnInit}).
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['selectedPageName'] &&
      !changes['selectedPageName'].firstChange
    ) {
      this.rebuildMenuItems();
    }
  }

  private rebuildMenuItems(): void {
    this.isSubTenant = this.userService.isSubTenant;
    this.isSuperTenant = this.userService.isSuperTenant;
    if (this.selectedPageName === TopLevelMenuItems.FINANCING) {
      this.menuItems = this.getFinancingMenuItems();
    } else {
      this.menuItems = this.getAdminMenuItems();
    }
  }

  get env() {
    return environment.profile;
  }

  /** Landmark label for the sidebar navigation (WCAG 2.4.1). */
  get navigationAriaLabel(): string {
    return this.selectedPageName === TopLevelMenuItems.FINANCING
      ? 'Financing navigation'
      : 'Admin navigation';
  }

  linkTestIdSegment(label: string): string {
    return label
      .toLowerCase()
      .trim()
      .replaceAll(/\s+/g, '-')
      .replaceAll(/[^a-z0-9-]/g, '');
  }

  getAdminMenuItems(): IPageNavItem[] {
    return [
      {
        label: 'General',
        route: '/secure/admin/general-setup',
        visible: true,
      },
      {
        label: 'Tenants',
        route: '/secure/admin/tenants',
        visible: this.isSuperTenant,
      },
      {
        label: 'Info',
        route: '/secure/admin/info-summary',
        visible: this.isSuperTenant,
      },
      {
        label: 'Batch Trigger',
        route: '/secure/admin/batch-trigger',
        visible: this.isSuperTenant,
      },
      {
        label: 'Originators',
        route: '/secure/admin/originators',
        visible: !this.isSubTenant && !this.isSuperTenant,
      },
      {
        label: 'Index Setup',
        route: '/secure/admin/index-setup',
        visible: !this.isSubTenant && !this.isSuperTenant,
      },
      {
        label: 'Investors',
        route: '/secure/admin/investors',
        visible: !this.isSubTenant && !this.isSuperTenant,
      },
      {
        label: 'Wire Recipients',
        route: '/secure/admin/wire-recipients',
        visible: !this.isSubTenant && !this.isSuperTenant,
      },
      {
        label: 'Templates',
        route: '/secure/admin/templates',
        visible: !this.isSubTenant && !this.isSuperTenant,
      },
      {
        label: 'Conditions',
        route: '/secure/admin/conditions',
        visible: !this.isSubTenant && !this.isSuperTenant,
      },
      {
        label: 'Agility Connect',
        route: '',
        visible: !this.isSubTenant && !this.isSuperTenant,
        expanded: true,
        children: [
          {
            label: 'Shipping',
            route: '/secure/admin/shipping',
            visible: true,
          },
          {
            label: 'Wire Recipient Verification',
            route: '/secure/admin/wire-recipient-verification',
            visible: true,
          },
        ],
      },
    ];
  }

  getFinancingMenuItems(): IPageNavItem[] {
    const showSources =
      !this.userService.isSubTenant &&
      !this.userService.isSuperTenant &&
      (this.env == 'dev' || this.env == 'qa') &&
      this.userService.checkFeatureFlag(TopLevelMenuItems.FINANCING);

    return [
      {
        label: 'Financing Sources',
        route: '/secure/financing/financing-sources',
        visible: showSources,
      },
      {
        label: 'FS WHP Allocation',
        route: '/secure/financing/fs-whp-allocation',
        visible: showSources,
      },
    ];
  }
}
